import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { useTranslation } from 'react-i18next'
import WizardTextField from '@/components/researcher/wizard/WizardTextField'
import { makeOption, type EditableOption } from './types'

type OptionsEditorProps = {
  options: EditableOption[]
  onChange: (next: EditableOption[]) => void
  /** Show an editable numeric value field per option. */
  showValues?: boolean
  /** Bullet style: single-choice (radio) vs multi/rank (checkbox). */
  marker?: 'radio' | 'check'
}

/**
 * Edits a question's answer options as individual rows — add, edit and remove
 * each one. Replaces the old "one option per line / comma separated" textarea.
 */
export default function OptionsEditor({ options, onChange, showValues, marker = 'radio' }: OptionsEditorProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('questions')
  const Marker = marker === 'check' ? CheckBoxOutlineBlankIcon : RadioButtonUncheckedIcon

  const update = (i: number, patch: Partial<EditableOption>) =>
    onChange(options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)))
  const remove = (i: number) => onChange(options.filter((_, idx) => idx !== i))
  const add = () => onChange([...options, makeOption(options.length, !!showValues)])

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: c.gray50,
        border: `1px solid ${c.ink08}`,
        borderRadius: `${theme.affexy.radii.md - 4}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: c.ink48 }}>
        {t('options')}
      </Typography>

      <Stack spacing={1}>
        {options.map((option, i) => (
          <Box key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Marker sx={{ fontSize: 18, color: c.ink32, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <WizardTextField
                value={option.text}
                onChange={(e) => update(i, { text: e.target.value })}
                placeholder={t('optionPlaceholder', { number: i + 1 })}
              />
            </Box>
            {showValues && (
              <Box sx={{ width: 84 }}>
                <WizardTextField
                  type="number"
                  value={option.value ?? 0}
                  onChange={(e) => update(i, { value: parseInt(e.target.value) || 0 })}
                  placeholder={t('value')}
                />
              </Box>
            )}
            <IconButton
              aria-label="remove option"
              size="small"
              onClick={() => remove(i)}
              disabled={options.length <= 1}
              sx={{ color: c.ink48, flexShrink: 0 }}
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>
        ))}
      </Stack>

      <Button
        size="small"
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={add}
        sx={{ alignSelf: 'flex-start', color: c.coral }}
      >
        {t('addOption')}
      </Button>
    </Box>
  )
}
